import React, { useState, useEffect } from 'react';
import usePermissions from 'hooks/permissions-hooks';
import AppLoader from 'components/basic/AppLoader';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { useItems } from 'hooks/atoms/items-hook';
import { parseDate } from 'components/business/consents/ConsentsTable';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Select, Typography, FormControl, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, Button, TableSortLabel, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import * as XLSX from 'xlsx';
import { roles } from '../cassandra-shared-js/roles';

const useStyles = makeStyles({
  title: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 35,
  },
  subtitle: {
    margin: '0px 0px 5px 5px',
    color: 'rgb(100, 141, 174)',
    fontSize: 18,
  },
  button: {
    margin: '6px 15px 0px 0px',
    fontSize: 18,
    borderColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    backgroundColor: 'rgb(100, 141, 174)',
    '&:hover': {
      backgroundColor: 'rgb(41, 58, 136)',
    },
  },
  selectLocation: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '0px 0px 20px 5px',
  },
  searchBox: {
    fontSize: 18,
    color: 'rgb(100, 141, 174)',
    margin: '6px 0px 0px 5px',
  },
  tableContainer: {
    margin: 'auto',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: 'rgb(100, 141, 174)',
    color: '#f0f4f8',
    fontWeight: 600,
    fontSize: 16,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
});

function ManagerSerialPage() {
  const classes = useStyles();
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ country, setCountry ] = useState('GR');
  const [ thematic, setThematic ] = useState('All');
  const { userHasRole, role } = usePermissions();
  const [ searchSerial, setSearchSerial ] = useState('');

  // Select Country
  useEffect(() => {
    if (userHasRole(roles.AL_MANAGER)) {
      setCountry('AL');
    } else if (userHasRole(roles.ADMINISTRATOR_ROLE)) {
      setCountry('GR');
    }
    refetch();
  }, [ role ]);

  // get serials

  const {
    countFiltered,
    items: serials,
    itemsLoading: serialsLoading,
    refetch,
    itemsFetching,
    itemsError: serialsError,
    pagination,
  } = useItems(CollectionKeys.SERIAL_NUMBER, { filterQuery: {
    country,
    ...filterQuery,
  } });

  // all serials for download
  const {
    items: allSerials,
    itemsLoading: allSerialsLoading,
    itemsError: allSerialsError,
  } = useItems(CollectionKeys.SERIAL_NUMBER, { filterQuery: {
    _limit: -1,
    country,
    ...filterQuery,
  } });

  if (serialsError) {
    return (
      <span>
        Error:
        {serialsError}
      </span>
    );
  }
  if (allSerialsError) {
    return (
      <span>
        Error:
        {allSerialsError}
      </span>
    );
  }

  // Download
  const downloadData = () => {
    function fitToColumn(arrayOfArray) {
      if (!arrayOfArray[0]) { // If file is empty
        return null;
      }
      return Object.keys(arrayOfArray[0]).map((key) => {
        return { wch: key.length };
      });
    }

    if (!allSerialsLoading) {
      const data = allSerials.map((serial) => {
        const obj = {
          'Serial Number': serial.serial,
          'Thematic Package': serial?.thematic_package?.name,
          Status: serial?.status,
          'Created At': parseDate(serial?.created_at),
          'Updated At': parseDate(serial?.updated_at),
        };
        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet['!cols'] = fitToColumn(data);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, 'Serial Numbers.xlsx');
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = pagination.sort.orderBy === property && pagination.sort.order === 'asc';
    pagination.setSort(property, isAsc ? 'desc' : 'asc');
    pagination.setPage(0);
  };

  const handleSearch = (value) => {
    const newFilter = { serial_contains: value };
    setFilterQuery({ ...filterQuery, ...newFilter });
    refetch();
  };

  const handleThematic = (value) => {
    setThematic(value);
    if (value !== 'All') {
      setFilterQuery({ ...filterQuery, 'thematic_package.real_name_eq': value });
    } else {
      delete filterQuery['thematic_package.real_name_eq'];
      setFilterQuery(filterQuery);
    }
    refetch();
  };

  // Start search with Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearchSerial(e.target.value);
      handleSearch(searchSerial);
    }
  };

  return (
    <Grid container spacing={ 2 } alignItems='center'>
      <Grid item xs={ 12 }>
        <div className={ classes.title }>
          Serial Numbers
        </div>
        <div className={ classes.subtitle }>
          Entries:
          {' '}
          {countFiltered}
        </div>
      </Grid>
      {userHasRole(roles.ADMINISTRATOR_ROLE)
      && (
        <Grid item xs={ 6 } md={ 2 } display='flex'>
          <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
            <Typography
              variant='subtitle'
              id='location-select-label'
            >
              Location:
            </Typography>
            <Select
              variant='outlined'
              size='small'
              labelId='country-select-label'
              id='country-select'
              value={ country }
              onChange={ (e) => { setCountry(e.target.value); refetch(); } }
            >
              <MenuItem value='GR'>Greece</MenuItem>
              <MenuItem value='AL'>Albania</MenuItem>
              <MenuItem value='IN'>India</MenuItem>
              <MenuItem value='IQ'>Iraq</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={ 6 } md={ 2 } display='flex'>
        <FormControl style={ { width: '100%' } } className={ classes.selectLocation }>
          <Typography
            variant='subtitle'
            id='thematic-select-label'
          >
            Thematic Package:
          </Typography>
          <Select
            variant='outlined'
            size='small'
            labelId='thematic-select-label'
            id='thematic-select'
            value={ thematic }
            onChange={ (e) => {
              handleThematic(e.target.value);
            } }
          >
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='iDNA PGx CNS Kit'>iDNA PGx kit</MenuItem>
            {country === 'GR' && <MenuItem value='iDNA Test Vitamins & Minerals'>Vitamins kit</MenuItem>}
            <MenuItem value='iDNA Cardio Health'>iDNA Cardio Health kit</MenuItem>
            <MenuItem value='NutriGenetix Laboratories kit'>NutriGenetix Laboratories kit</MenuItem>
            {country === 'GR' && <MenuItem value='iDNA NutriGenetix kit'>NutriGenetix kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Nutrition Test'>Nutritional kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Test 3in1'>3-in-1 kit</MenuItem>}
            {country === 'GR' && <MenuItem value='iDNA Biological Age'>Biological Age kit</MenuItem>}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={ 6 } md={ 2 } display='flex'>
        <TextField
          className={ classes.searchBox }
          id='search-bar'
          size='medium'
          value={ searchSerial }
          onChange={ (e) => { setSearchSerial(e.target.value); } }
          onKeyPress={ (e) => { handleKeyPress(e); } }
          label='Enter Serial Number'
          variant='outlined'
          defaultValue='20px Text'
        />
      </Grid>
      <Grid item xs={ 6 } md={ 6 } display='flex'>
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => { handleSearch(searchSerial); } }
        >
          <SearchIcon />
          SEARCH
        </Button>

        { searchSerial.length > 0
      && (
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ () => {
            setSearchSerial('');
            setFilterQuery({});
          } }
        >
          CLEAR
        </Button>
      )}
        <Button
          className={ classes.button }
          variant='outlined'
          size='large'
          onClick={ downloadData }
        >
          Download
        </Button>
      </Grid>
      <Grid item xs={ 12 } md={ 12 }>
        <Paper>
          <TableContainer className={ classes.tableContainer }>
            { (serialsLoading || itemsFetching) ? <AppLoader />
              : (
                <Table className={ classes.table }>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={ classes.headerCell }
                        sortDirection={ pagination.sort.orderBy === 'serial' ? pagination.sort.order : false }
                      >
                        <TableSortLabel
                          active={ pagination.sort.orderBy === 'serial' }
                          direction={ pagination.sort.orderBy === 'serial' ? pagination.sort.order : 'asc' }
                          onClick={ () => { return handleRequestSort('serial'); } }
                        >
                          Serial Number
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={ classes.headerCell }>Thematic Package</TableCell>
                      <TableCell className={ classes.headerCell }>Status</TableCell>
                      <TableCell className={ classes.headerCell }>Created At</TableCell>

                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {serials.map((serial) => {
                      return (
                        <TableRow key={ serial.id } className={ classes.row }>
                          <TableCell>{serial?.serial}</TableCell>
                          <TableCell>{serial?.thematic_package?.name}</TableCell>
                          <TableCell>{serial?.status}</TableCell>
                          <TableCell>{parseDate(serial?.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
          </TableContainer>

          <TablePagination
            component='div'
            count={ countFiltered }
            page={ pagination.page }
            onChangePage={ (e, p) => {
              pagination.setPage(p);
            } }
            rowsPerPage={ pagination.limit }
            onChangeRowsPerPage={
              (e) => {
                const newLimit = parseInt(e.target.value, 10);
                pagination.setLimit(newLimit);
                pagination.setPage(0);
              }
            }
            rowsPerPageOptions={ [ 5, 10, 25 ] }
          />
        </Paper>
      </Grid>
    </Grid>
  );
}

export default ManagerSerialPage;
